<template>
  <layout-report :renderBackAndForthFooter="false" :isProgressDone="true" :showFooter="false">
    <v-card-title class="text-h4 px-0 text-truncate"> </v-card-title>
    <v-container class="padded" v-if="!isLoading">
      <v-row>
        <v-col cols="12">
          <v-card flat>
            <v-card-text class="pl-0">
              <a @click="goToChargingStationOverview">
                <v-icon>mdi-menu-left</v-icon>{{ $t("components.thg.ThgChargingStationDetail.back") }}
              </a>
            </v-card-text>
            <v-card-title class="text-h4 px-0 text-truncate">
              {{ chargingStation?.name }}
              <v-spacer />
              <v-card-actions>
                <v-btn class="px-4" small elevation="0" color="primary" @click="isUpdateDialogActive = true">
                  {{ $t("change") }}
                </v-btn>
                <v-btn
                  class="px-4"
                  color="error"
                  small
                  outlined
                  v-if="!meterReadings?.length"
                  @click="isDeleteDialogActive = true"
                >
                  {{ $t("delete") }}
                </v-btn>
              </v-card-actions>
            </v-card-title>

            <v-card-subtitle class="pl-0">
              {{ $t("components.thg.ThgChargingStationDetail.created", { date: chargingStationTimeStamp }) }}
            </v-card-subtitle>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <thg-charging-station-card
            :selection="chargingStation"
            :meterReadings="meterReadings"
            :loading="isLoading"
          ></thg-charging-station-card>
          <Debug :debug="chargingStation" />
          <Debug :debug="meterReadings" />
        </v-col>
      </v-row>
    </v-container>
    <v-container class="padded" v-else>
      <v-row>
        <v-card flat max-width="500">
          <v-card-text>
            <a @click="goToChargingStationOverview"><v-icon>mdi-menu-left</v-icon>zurück zur Übersicht</a>
            <v-skeleton-loader class="my-4" type="heading"></v-skeleton-loader>
            <v-skeleton-loader type="text"></v-skeleton-loader>
          </v-card-text>
        </v-card>
      </v-row>
      <v-row>
        <v-col offset="0" cols="12">
          <v-card class="mb-10">
            <v-skeleton-loader type="table-heading, table-tbody, table-tfoot"></v-skeleton-loader>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
    <!-- Delete -->
    <confirm-action-dialog
      :isDialogActive.sync="isDeleteDialogActive"
      :title="$t('components.thg.ThgChargingStationDetail.confirmDeleteTitle')"
      :subtitle="$t('components.thg.ThgChargingStationDetail.confirmDeleteSubtitle')"
      :leftLoading="isDeleteDialogLoading"
      :rightLoading="isDeleteDialogLoading"
      @leftClick="isDeleteDialogActive = false"
      @rightClick="deleteChargingStation"
    >
    </confirm-action-dialog>
    <!-- Update -->
    <confirm-action-dialog
      :isDialogActive.sync="isUpdateDialogActive"
      :title="$t('components.thg.ThgChargingStationDetail.confirmUpdateTitle')"
      :subtitle="$t('components.thg.ThgChargingStationDetail.confirmUpdateSubtitle')"
      :leftLoading="isUpdateDialogLoading"
      :rightLoading="isUpdateDialogLoading"
      :hideLeft="true"
      :hideRight="true"
    >
      <thg-charging-station-form
        :isEdit="true"
        :value="chargingStation"
        @save="updateChargingStation"
      ></thg-charging-station-form>
    </confirm-action-dialog>
  </layout-report>
</template>

<script lang="ts">
import ThgChargingStationCard from "@/components/thg/ThgChargingStationCard.vue";
import ThgChargingStationForm from "@/components/thg/ThgChargingStationForm.vue";
import Debug from "@/components/utility/Debug.vue";
import LayoutReport from "@/layouts/LayoutReport.vue";
import ConfirmActionDialog from "@/components/utility/ConfirmActionDialog.vue";
import { HttpException } from "@/lib/exceptions/http";
import { PartnerColor } from "@/lib/partnerColor";
import { ThgChargingStationViewModelGen, ThgThgMeterReadingViewModelGen } from "@/services/thg/v1/data-contracts";
import { ChargingStationModule } from "@/store/modules/charging-station.store";
import { MeterReadingModule } from "@/store/modules/meter-reading.store";

import { Component, Vue } from "vue-property-decorator";
import { handleError } from "@/lib/utility/handleError";
import { ChargingStationUiDto } from "@/store/models/thg/create-charging-station";

@Component({
  components: {
    ThgChargingStationCard,
    LayoutReport,
    ConfirmActionDialog,
    ThgChargingStationForm,
    Debug
  }
})
export default class ThgChargingStationDetail extends Vue {
  isLoading = true;

  partnerColor = new PartnerColor();

  // Dialogs
  isDeleteDialogActive = false;
  isDeleteDialogLoading = false;
  isUpdateDialogActive = false;
  isUpdateDialogLoading = false;

  async mounted() {
    try {
      this.isLoading = true;
      await MeterReadingModule.getMeterReadingsByChargingStation(this.$route.params.chargingStationId);
      await ChargingStationModule.getChargingStation(this.$route.params.chargingStationId);
    } catch (error) {
      this.$log.error(error);
      if (error instanceof HttpException) {
        this.$toast.error(error.message);
      }
    } finally {
      this.isLoading = false;
    }
  }

  async updateChargingStation(chargingStation: ChargingStationUiDto) {
    if (!this.chargingStation) {
      return;
    }

    this.isUpdateDialogLoading = true;

    try {
      await ChargingStationModule.updateChargingStation({
        id: this.chargingStation.id,
        data: chargingStation.toUpdateMyChargingStationShort()
      });
      this.$toast.success("👍");
    } catch (error) {
      handleError(error);
    } finally {
      this.isUpdateDialogActive = false;
      this.isUpdateDialogLoading = false;
    }
  }

  async deleteChargingStation() {
    if (!this.chargingStation) {
      return;
    }

    this.isDeleteDialogLoading = true;

    try {
      await ChargingStationModule.removeChargingStation(this.chargingStation.id);
      this.$toast.success("👍");
      this.goToChargingStationOverview();
    } catch (error) {
      handleError(error);
    } finally {
      this.isDeleteDialogActive = false;
      this.isDeleteDialogLoading = false;
    }
  }

  get chargingStation(): ThgChargingStationViewModelGen | undefined {
    return ChargingStationModule.activeChargingStation;
  }

  get meterReadings(): ThgThgMeterReadingViewModelGen[] | undefined {
    return MeterReadingModule.meterReadings;
  }

  goToChargingStationOverview() {
    this.$router.push({ path: "/charging-station" });
  }

  get chargingStationTimeStamp(): any {
    const timestamp = this.chargingStation?.timestamp as any;
    return new Date(timestamp.created).toLocaleString();
  }
}
</script>
